import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
/* global gdAlertify */

// Página oculta (no está en ningún menú): tokens de conexión del servidor MCP y log de uso.
// El backend solo responde a los usuarios listados en mcp.usuarios_panel (global.php).

const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

const copiar = async (texto) => {
    try {
        await navigator.clipboard.writeText(texto);
        gdAlertify.notiSuccess("Copiado.");
    } catch (e) {
        gdAlertify.notiError("No se pudo copiar.");
    }
};

const NIVEL_BADGE = { admin: "bg-danger", consulta: "bg-primary", rechazado: "bg-dark" };

const TokenRow = ({ titulo, descripcion, token, url }) => {
    const [visible, setVisible] = useState(false);
    const comando = `claude mcp add --transport http sk-medical ${url} --header "Authorization: Bearer ${token}"`;

    return (
        <div className="mb-4">
            <div className="fw-bold">{titulo}</div>
            <div className="text-muted small mb-2">{descripcion}</div>
            {token ? (
                <>
                    <div className="input-group input-group-sm mb-2">
                        <input
                            type={visible ? "text" : "password"}
                            className="form-control font-monospace"
                            value={token}
                            readOnly
                        />
                        <button className="btn btn-outline-secondary" onClick={() => setVisible(!visible)} title={visible ? "Ocultar" : "Mostrar"}>
                            <i className={`bi ${visible ? "bi-eye-slash" : "bi-eye"}`}></i>
                        </button>
                        <button className="btn btn-outline-secondary" onClick={() => copiar(token)} title="Copiar token">
                            <i className="bi bi-clipboard"></i>
                        </button>
                    </div>
                    <div className="d-flex gap-2 align-items-start">
                        <code className="small bg-light border rounded p-2 flex-grow-1 text-break">
                            {visible ? comando : comando.replace(token, "••••••••")}
                        </code>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => copiar(comando)} title="Copiar comando">
                            <i className="bi bi-clipboard"></i>
                        </button>
                    </div>
                </>
            ) : (
                <div className="text-muted small">No configurado en global.php.</div>
            )}
        </div>
    );
};

const McpPanel = () => {
    const [config, setConfig] = useState(null);
    const [sinAcceso, setSinAcceso] = useState("");
    const [filtros, setFiltros] = useState({ desde: haceDias(30), hasta: hoy(), nivel: "", ok: "" });
    const [log, setLog] = useState({ resumen: [], registros: [] });
    const [loadingLog, setLoadingLog] = useState(false);
    const [errorLog, setErrorLog] = useState("");

    const cargarConfig = async () => {
        try {
            const { data } = await axiosInstance.get("/mcp-panel/config");
            setConfig(data.data);
            return true;
        } catch (error) {
            setSinAcceso(error.response?.data?.message || "No se pudo cargar la configuración del MCP.");
            return false;
        }
    };

    const cargarLog = async () => {
        setLoadingLog(true);
        setErrorLog("");
        try {
            const { data } = await axiosInstance.get("/mcp-panel/log", { params: filtros });
            if (data.error) {
                setErrorLog(data.message);
                setLog({ resumen: [], registros: [] });
            } else {
                setLog(data.data);
            }
        } catch (error) {
            setErrorLog(error.response?.data?.message || "No se pudo cargar el log.");
        } finally {
            setLoadingLog(false);
        }
    };

    useEffect(() => {
        cargarConfig().then((ok) => ok && cargarLog());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFiltro = (e) => setFiltros({ ...filtros, [e.target.name]: e.target.value });

    const columnasResumen = [
        { title: "Herramienta", name: "herramienta" },
        { title: "Llamadas", name: "llamadas" },
        { title: "Errores", name: "errores", render: (r) => <span className={Number(r.errores) > 0 ? "text-danger fw-bold" : ""}>{r.errores}</span> },
        { title: "Duración prom. (ms)", name: "duracion_promedio_ms" },
        { title: "Última", name: "ultima" },
    ];

    const columnasLog = [
        { title: "Fecha", name: "fecha" },
        { title: "Nivel", name: "nivel", render: (r) => <span className={`badge ${NIVEL_BADGE[r.nivel] || "bg-secondary"}`}>{r.nivel}</span> },
        { title: "Herramienta", name: "herramienta" },
        {
            title: "Argumentos",
            name: "argumentos",
            render: (r) => <code className="small text-break" style={{ whiteSpace: "pre-wrap" }}>{r.argumentos}</code>,
        },
        { title: "OK", name: "ok", render: (r) => (Number(r.ok) === 1 ? <i className="bi bi-check-circle text-success"></i> : <i className="bi bi-x-circle text-danger"></i>) },
        { title: "Filas", name: "filas" },
        { title: "ms", name: "duracion_ms" },
        { title: "Error", name: "error", render: (r) => <span className="text-danger small">{r.error}</span> },
        { title: "IP", name: "ip" },
        { title: "Cliente", name: "cliente", render: (r) => <span className="small text-muted">{r.cliente}</span> },
    ];

    if (sinAcceso) {
        return (
            <div className="container-fluid py-4">
                <h4 className="fw-bold mb-3"><i className="bi bi-robot me-2"></i>MCP</h4>
                <div className="alert alert-warning">{sinAcceso}</div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <h4 className="fw-bold mb-2"><i className="bi bi-robot me-2"></i>MCP — Acceso IA</h4>
            <p className="text-muted">
                Servidor MCP de solo lectura para consultar el sistema desde una IA. Estos tokens dan acceso a datos de
                pacientes y auxiliares: no los comparta fuera del equipo. Para cambiarlos, edite <code>mcp</code> en
                <code> global.php</code> del backend.
            </p>

            {config && (
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-body">
                        <div className="d-flex flex-wrap gap-3 mb-3 small">
                            <span>
                                Estado:{" "}
                                {config.habilitado
                                    ? <span className="badge bg-success">Habilitado</span>
                                    : <span className="badge bg-secondary">Deshabilitado</span>}
                            </span>
                            <span>URL: <code>{config.url}</code></span>
                            <span>
                                Conexión BD:{" "}
                                {config.db_propia
                                    ? <span className="badge bg-success">usuario propio</span>
                                    : <span className="badge bg-warning text-dark">la del backend (READ ONLY por sesión)</span>}
                            </span>
                        </div>
                        <TokenRow
                            titulo="Token consulta"
                            descripcion="Buscar pacientes/auxiliares, turnos, adicionales, historial, honorarios y cobranza."
                            token={config.token}
                            url={config.url}
                        />
                        <TokenRow
                            titulo="Token admin"
                            descripcion="Todo lo anterior + validar período, ver esquema y SQL libre de solo lectura."
                            token={config.token_admin}
                            url={config.url}
                        />
                    </div>
                </div>
            )}

            <div className="card shadow-sm border-0">
                <div className="card-body">
                    <h5 className="fw-bold mb-3">Log de uso</h5>
                    <div className="row g-2 align-items-end mb-3">
                        <div className="col-auto">
                            <label className="form-label small mb-0">Desde</label>
                            <input type="date" name="desde" className="form-control form-control-sm" value={filtros.desde} onChange={onFiltro} />
                        </div>
                        <div className="col-auto">
                            <label className="form-label small mb-0">Hasta</label>
                            <input type="date" name="hasta" className="form-control form-control-sm" value={filtros.hasta} onChange={onFiltro} />
                        </div>
                        <div className="col-auto">
                            <label className="form-label small mb-0">Nivel</label>
                            <select name="nivel" className="form-select form-select-sm" value={filtros.nivel} onChange={onFiltro}>
                                <option value="">Todos</option>
                                <option value="consulta">Consulta</option>
                                <option value="admin">Admin</option>
                                <option value="rechazado">Rechazado</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <label className="form-label small mb-0">Resultado</label>
                            <select name="ok" className="form-select form-select-sm" value={filtros.ok} onChange={onFiltro}>
                                <option value="">Todos</option>
                                <option value="1">OK</option>
                                <option value="0">Con error</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-sm btn-primary" onClick={cargarLog} disabled={loadingLog}>
                                <i className="bi bi-search me-1"></i>Buscar
                            </button>
                        </div>
                    </div>

                    {errorLog && <div className="alert alert-warning py-2">{errorLog}</div>}

                    <h6 className="fw-bold">Resumen por herramienta</h6>
                    <div className="mb-4">
                        <GdTable columns={columnasResumen} data={log.resumen} loading={loadingLog} />
                    </div>

                    <h6 className="fw-bold">Llamadas ({log.registros.length})</h6>
                    <GdTable
                        columns={columnasLog}
                        data={log.registros}
                        loading={loadingLog}
                        stickyHeader={true}
                        height={600}
                        showCsvExport={true}
                        exportFilename="mcp-log.xlsx"
                    />
                </div>
            </div>
        </div>
    );
};

export default McpPanel;
